import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Shield, Plus, Pencil, UserCheck, UserX, Activity, Star, Copy, Link2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/CrudModule";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { useReference } from "@/context/ReferenceContext";
import LeadProviderManager from "@/components/LeadProviderManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ROLE_TONE = {
  admin: "bg-[#B28A3D]/12 text-[#7a5d27] ring-[#B28A3D]/30",
  ops_manager: "bg-[#17324D]/10 text-[#17324D] ring-[#17324D]/20",
  agent: "bg-[#315A7D]/10 text-[#2c5170] ring-[#315A7D]/25",
  bpo: "bg-amber-50 text-amber-700 ring-amber-200",
  future: "bg-slate-100 text-slate-600 ring-slate-200",
  vendor: "bg-emerald-50 text-emerald-700 ring-emerald-200",
};
const PERMS = {
  admin: "Full access including user management, settings, imports and all records.",
  ops_manager: "Manages all operational records, commissions and scorecards.",
  agent: "Manages assigned leads, quotes, applications and policies.",
  bpo: "Adds leads and completes intake. No commission or scorecard access.",
  future: "Read-only access to assigned leads and tasks.",
  vendor: "Restricted to the provider portal and only the business supplied by that lead source.",
};
const ROLES = ["admin", "ops_manager", "agent", "bpo", "bpo_closer", "future"];
const RESTRICTED = ["bpo", "future"];

const EMPTY = { name: "", email: "", role: "agent", title: "", manager: "", is_default_assignee: false, active: true };

export default function Team() {
  const { user } = useAuth();
  const { refresh } = useReference();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const isAdmin = user.role === "admin";

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, role: u.role, title: u.title || "",
      manager: u.manager || "", is_default_assignee: !!u.is_default_assignee, active: u.active !== false });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, form);
        toast.success("User updated");
      } else {
        const { data } = await api.post("/users", form);
        setInviteInfo({ email: form.email, link: data.invite_link || "", emailStatus: data.email_status });
        toast.success("User created. Secure invitation ready.");
      }
      setOpen(false);
      await load();
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    await api.put(`/users/${u.id}`, { active: !(u.active !== false) }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
    toast.success(u.active !== false ? "User deactivated" : "User reactivated");
    load(); refresh();
  };

  const copyInvite = async () => {
    if (!inviteInfo?.link) return;
    try {
      await navigator.clipboard.writeText(inviteInfo.link);
      toast.success("Invitation link copied");
    } catch (_) {
      toast.error("Could not copy automatically. Select and copy the link manually.");
    }
  };

  const internalUsers = users.filter((u) => u.role !== "vendor");
  const providers = users.filter((u) => u.role === "vendor");
  const managers = internalUsers.filter((u) => ["admin", "ops_manager"].includes(u.role)).map((u) => u.name);

  return (
    <div>
      <PageHeader title="Team" subtitle={isAdmin ? "Manage internal users, roles, access, and restricted lead-provider portals." : "Members and their role-based permissions."}>
        {isAdmin && (
          <Button onClick={openNew} data-testid="add-user-btn"><Plus className="mr-1.5 h-4 w-4" /> Add User</Button>
        )}
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {internalUsers.map((u) => {
          const inactive = u.active === false;
          return (
            <div key={u.id || u.email} className={`rounded-lg border bg-card p-5 ${inactive ? "border-border opacity-60" : "border-border"}`} data-testid={`team-${u.role}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary font-heading font-bold">
                    {u.avatar || u.name[0]}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 font-heading font-semibold">
                      {u.name}
                      {u.is_default_assignee && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" title="Default assignee" />}
                    </div>
                    <div className="text-xs text-muted-foreground">{u.title}</div>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(u)} className="rounded p-1.5 text-muted-foreground hover:text-primary hover:bg-secondary" data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => toggleActive(u)} className="rounded p-1.5 text-muted-foreground hover:bg-secondary" title={inactive ? "Reactivate" : "Deactivate"} data-testid={`toggle-user-${u.id}`}>
                      {inactive ? <UserCheck className="h-4 w-4 text-emerald-600" /> : <UserX className="h-4 w-4 text-red-500" />}
                    </button>
                  </div>
                )}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${ROLE_TONE[u.role] || ROLE_TONE.future}`}>
                  <Shield className="h-3 w-3" /> {ROLE_LABELS[u.role] || u.role}
                </span>
                {inactive
                  ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 ring-1 ring-slate-200">Inactive</span>
                  : <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200">Active</span>}
                {RESTRICTED.includes(u.role) && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700 ring-1 ring-red-200">No commissions</span>}
              </div>
              <p className="mt-3 text-xs text-muted-foreground leading-relaxed">{PERMS[u.role] || "Role-based access."}</p>
              <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {u.email}</span>
                <span className="flex items-center gap-1.5" title="Activity events"><Activity className="h-3.5 w-3.5" /> {u.activity_count ?? 0}</span>
              </div>
              {u.manager && <div className="mt-1 text-xs text-muted-foreground">Manager: {u.manager}</div>}
            </div>
          );
        })}
      </div>

      {isAdmin && <LeadProviderManager providers={providers} onCreated={load} />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit User" : "Add Team User"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Update profile, role and access."
                : "New users receive a secure invitation link and set their own password. No shared default password is used."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input data-testid="user-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Title</Label>
              <Input data-testid="user-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Manager</Label>
              <Select value={form.manager || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, manager: v === "__none__" ? "" : v }))}>
                <SelectTrigger data-testid="user-manager"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {managers.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <Label className="text-xs">Active</Label>
              <Switch data-testid="user-active" checked={form.active} onCheckedChange={(c) => setForm((f) => ({ ...f, active: c }))} />
            </div>
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 sm:col-span-2">
              <div>
                <Label className="text-xs">Default Assignee</Label>
                <p className="text-[11px] text-muted-foreground">New leads default to this team member.</p>
              </div>
              <Switch data-testid="user-default" checked={form.is_default_assignee} onCheckedChange={(c) => setForm((f) => ({ ...f, is_default_assignee: c }))} />
            </div>
            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="user-save">{saving ? "Saving…" : "Save User"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!inviteInfo} onOpenChange={(o) => !o && setInviteInfo(null)}>
        <DialogContent className="max-w-lg" data-testid="user-invite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="h-5 w-5" /> Secure Invitation Ready</DialogTitle>
            <DialogDescription>
              {inviteInfo?.email} must use this one-time link to choose a password and activate access. If email delivery is configured, the same invitation is also sent automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Invitation link</Label>
            <div className="flex gap-2">
              <Input readOnly value={inviteInfo?.link || ""} className="font-mono text-xs" data-testid="user-invite-link" />
              <Button type="button" variant="outline" onClick={copyInvite} disabled={!inviteInfo?.link} data-testid="user-invite-copy">
                <Copy className="mr-1.5 h-4 w-4" /> Copy
              </Button>
            </div>
            {!inviteInfo?.link && <p className="text-xs text-amber-700">The user was created, but no invitation link was returned. Use Agency Admin to resend the invitation.</p>}
          </div>
          <DialogFooter>
            <Button onClick={() => setInviteInfo(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
